<script setup lang="ts">
// NPM
import { RouterLink, useRouter } from 'vue-router';
import { useMq } from 'vue3-mq';
import Cookie from 'js-cookie';

// Components
import { BaseAvatar, BaseButton, BaseIcon } from '@magma-app/magma-lapilli';
import TheNavigationItem from '@/components/shell/TheNavigationItem.vue';

// Composables & Stores
import { useNavigationItems } from '@/composables/useNavigationItems';
import { useUserStore } from '@/stores/user';
const { navigationItems } = useNavigationItems();
const userStore = useUserStore();

// Plugins
const mq = useMq();
const router = useRouter();

// Props
defineProps({
	hasNavigation: {
		type: Boolean,
		default: true,
	},
});

// Methods
const onLogout = () => {
	localStorage.removeItem('jwtToken');
	localStorage.removeItem('auth');
	localStorage.removeItem('token');
	localStorage.removeItem('teamSpace');
	localStorage.removeItem('campaigns');
	Cookie.remove('jwtToken');

	router.push({ name: 'login' });
};
</script>

<template>
	<div
		class="hidden h-screen w-20 flex-col items-center justify-between py-8 lg:fixed lg:flex 2xl:w-64 2xl:items-start 2xl:px-4"
	>
		<div class="flex flex-col items-center 2xl:w-full 2xl:items-start">
			<router-link
				class="flex items-center 2xl:px-2"
				:to="{
					name: 'relations-empty-state',
				}"
			>
				<BaseAvatar
					:src="userStore.user?.organization?.logoUrl"
					:alt="'logo ' + userStore.user?.organization?.name"
					:fallback="userStore.user?.organization?.name.charAt(0)"
				/>

				<span
					class="foreground-emphasis hidden max-w-[150px] truncate px-2 text-mgm-txt-sm font-medium 2xl:block"
				>
					{{ userStore.user?.organization?.name }}
				</span>
			</router-link>

			<div v-if="hasNavigation" class="my-10 2xl:w-full">
				<TheNavigationItem
					v-for="navigationItem in navigationItems"
					:key="navigationItem.name"
					class="mt-1"
					:class="{ hidden: !navigationItem.enabled }"
					:icon="navigationItem.icon"
					:name="navigationItem.name"
					:to="navigationItem.to"
					exact-active-class
					sidebar
				/>
			</div>
		</div>

		<div class="flex flex-col items-center">
			<div
				v-if="userStore.referFormEnabled"
				class="mb-5 w-full cursor-pointer rounded-lg px-4 py-5 2xl:cursor-auto 2xl:bg-background-default"
			>
				<BaseAvatar
					class="mx-auto"
					:src="`/img/avatars/avatar_${Math.floor(Math.random() * 9) + 1}.png`"
					size="lg"
					@click="
						() => {
							if (mq.lgMinus) {
								$router.push({ name: 'referral' });
							}
						}
					"
				/>

				<p
					class="mt-4 hidden text-center text-mgm-txt-sm font-normal text-foreground-default 2xl:block"
				>
					{{
						`${$t('theSidebar.interested')} ${
							userStore.user?.organization?.name
						} ?`
					}}
				</p>

				<BaseButton
					class="mt-4 hidden w-full 2xl:block"
					:text="$t('generic.referCta')"
					@click="() => $router.push({ name: 'referral' })"
				/>
			</div>

			<RouterLink
				v-if="hasNavigation"
				class="group mb-4 flex h-10 w-12 content-center items-center justify-center rounded-[40px] px-3 py-2 text-sm font-medium hover:bg-background-default 2xl:w-56 2xl:justify-start"
				:to="{ name: 'settings-profile' }"
				exact-active-class="bg-background-default hover:bg-background-default "
			>
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clip-path="url(#clip0_507_2776)">
						<path
							d="M7.99984 10C9.10441 10 9.99984 9.10459 9.99984 8.00002C9.99984 6.89545 9.10441 6.00002 7.99984 6.00002C6.89527 6.00002 5.99984 6.89545 5.99984 8.00002C5.99984 9.10459 6.89527 10 7.99984 10Z"
							stroke="#5F6C85"
							stroke-width="1.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
						<path
							d="M12.9332 10C12.8444 10.2011 12.818 10.4241 12.8572 10.6404C12.8964 10.8567 12.9995 11.0562 13.1532 11.2134L13.1932 11.2534C13.3171 11.3772 13.4155 11.5242 13.4826 11.6861C13.5497 11.848 13.5842 12.0215 13.5842 12.1967C13.5842 12.3719 13.5497 12.5454 13.4826 12.7073C13.4155 12.8691 13.3171 13.0162 13.1932 13.14C13.0693 13.264 12.9223 13.3623 12.7604 13.4294C12.5986 13.4965 12.4251 13.5311 12.2498 13.5311C12.0746 13.5311 11.9011 13.4965 11.7393 13.4294C11.5774 13.3623 11.4303 13.264 11.3065 13.14L11.2665 13.1C11.1094 12.9463 10.9098 12.8432 10.6936 12.804C10.4773 12.7648 10.2542 12.7913 10.0532 12.88C9.85599 12.9645 9.68783 13.1049 9.56938 13.2837C9.45093 13.4626 9.38736 13.6722 9.3865 13.8867V14C9.3865 14.3536 9.24603 14.6928 8.99598 14.9428C8.74593 15.1929 8.40679 15.3334 8.05317 15.3334C7.69955 15.3334 7.36041 15.1929 7.11036 14.9428C6.86031 14.6928 6.71984 14.3536 6.71984 14V13.94C6.71468 13.7194 6.64325 13.5054 6.51484 13.3258C6.38644 13.1463 6.20699 13.0095 5.99984 12.9334C5.79876 12.8446 5.57571 12.8181 5.35945 12.8574C5.14318 12.8966 4.94362 12.9997 4.7865 13.1534L4.7465 13.1934C4.62267 13.3173 4.47562 13.4157 4.31376 13.4828C4.15189 13.5499 3.97839 13.5844 3.80317 13.5844C3.62795 13.5844 3.45445 13.5499 3.29258 13.4828C3.13072 13.4157 2.98367 13.3173 2.85984 13.1934C2.73587 13.0695 2.63752 12.9225 2.57042 12.7606C2.50333 12.5987 2.46879 12.4252 2.46879 12.25C2.46879 12.0748 2.50333 11.9013 2.57042 11.7394C2.63752 11.5776 2.73587 11.4305 2.85984 11.3067L2.89984 11.2667C3.05353 11.1096 3.15663 10.91 3.19584 10.6937C3.23505 10.4775 3.20858 10.2544 3.11984 10.0534C3.03533 9.85617 2.89501 9.68801 2.71615 9.56956C2.53729 9.45111 2.32769 9.38754 2.11317 9.38669H1.99984C1.64622 9.38669 1.30708 9.24621 1.05703 8.99616C0.80698 8.74611 0.666504 8.40698 0.666504 8.05335C0.666504 7.69973 0.80698 7.36059 1.05703 7.11055C1.30708 6.8605 1.64622 6.72002 1.99984 6.72002H2.05984C2.2805 6.71486 2.49451 6.64343 2.67404 6.51503C2.85357 6.38662 2.99031 6.20718 3.0665 6.00002C3.15525 5.79894 3.18172 5.57589 3.14251 5.35963C3.10329 5.14336 3.0002 4.94381 2.8465 4.78669L2.8065 4.74669C2.68254 4.62286 2.58419 4.4758 2.51709 4.31394C2.44999 4.15208 2.41546 3.97857 2.41546 3.80335C2.41546 3.62813 2.44999 3.45463 2.51709 3.29277C2.58419 3.1309 2.68254 2.98385 2.8065 2.86002C2.93033 2.73605 3.07739 2.63771 3.23925 2.57061C3.40111 2.50351 3.57462 2.46897 3.74984 2.46897C3.92506 2.46897 4.09856 2.50351 4.26042 2.57061C4.42229 2.63771 4.56934 2.73605 4.69317 2.86002L4.73317 2.90002C4.89029 3.05371 5.08985 3.15681 5.30611 3.19602C5.52237 3.23524 5.74543 3.20876 5.9465 3.12002H5.99984C6.19702 3.03551 6.36518 2.89519 6.48363 2.71633C6.60208 2.53747 6.66565 2.32788 6.6665 2.11335V2.00002C6.6665 1.6464 6.80698 1.30726 7.05703 1.05721C7.30708 0.807163 7.64622 0.666687 7.99984 0.666687C8.35346 0.666687 8.6926 0.807163 8.94265 1.05721C9.19269 1.30726 9.33317 1.6464 9.33317 2.00002V2.06002C9.33403 2.27454 9.39759 2.48414 9.51604 2.663C9.63449 2.84186 9.80266 2.98218 9.99984 3.06669C10.2009 3.15543 10.424 3.1819 10.6402 3.14269C10.8565 3.10348 11.0561 3.00038 11.2132 2.84669L11.2532 2.80669C11.377 2.68272 11.5241 2.58437 11.6859 2.51727C11.8478 2.45018 12.0213 2.41564 12.1965 2.41564C12.3717 2.41564 12.5452 2.45018 12.7071 2.51727C12.869 2.58437 13.016 2.68272 13.1398 2.80669C13.2638 2.93052 13.3622 3.07757 13.4293 3.23943C13.4963 3.4013 13.5309 3.5748 13.5309 3.75002C13.5309 3.92524 13.4963 4.09874 13.4293 4.26061C13.3622 4.42247 13.2638 4.56952 13.1398 4.69335L13.0998 4.73335C12.9461 4.89047 12.843 5.09003 12.8038 5.30629C12.7646 5.52256 12.7911 5.74561 12.8798 5.94669V6.00002C12.9643 6.1972 13.1047 6.36537 13.2835 6.48382C13.4624 6.60227 13.672 6.66583 13.8865 6.66669H13.9998C14.3535 6.66669 14.6926 6.80716 14.9426 7.05721C15.1927 7.30726 15.3332 7.6464 15.3332 8.00002C15.3332 8.35364 15.1927 8.69278 14.9426 8.94283C14.6926 9.19288 14.3535 9.33335 13.9998 9.33335H13.9398C13.7253 9.33421 13.5157 9.39778 13.3369 9.51623C13.158 9.63468 13.0177 9.80284 12.9332 10Z"
							stroke="#5F6C85"
							stroke-width="1.5"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</g>
					<defs>
						<clipPath id="clip0_507_2776">
							<rect width="16" height="16" fill="white" />
						</clipPath>
					</defs>
				</svg>

				<span class="hidden pl-2 2xl:inline-block">
					{{ $t('generic.settings') }}
				</span>
			</RouterLink>

			<div class="w-full border-b border-b-border-subtle"></div>

			<div
				class="flex w-full justify-center px-2 pt-4 2xl:justify-between 2xl:px-0"
			>
				<router-link
					class="hidden items-center 2xl:flex"
					:to="{
						name: 'settings-profile',
					}"
				>
					<BaseAvatar
						v-if="userStore.user"
						:src="userStore.user.profilePictureUrl"
						:alt="`${$t('generic.profilePicture')} ${
							userStore.user.firstname
						} ${userStore.user.lastname}`"
						:fallback="
							userStore.user.firstname.charAt(0) +
							userStore.user.lastname.charAt(0)
						"
					/>

					<span class="max-w-[135px] truncate px-2">
						{{ `${userStore.user?.firstname} ${userStore.user?.lastname}` }}
					</span>
				</router-link>

				<button
					class="group flex h-10 w-12 items-center justify-center rounded-[40px] text-sm font-medium hover:bg-background-default"
					@click="onLogout"
				>
					<BaseIcon icon="log-out" />
				</button>
			</div>
		</div>
	</div>
</template>
